import React from 'react';
import { Activity } from '@graphql/generated-graphql';
import { ActiveLink } from '@components/common/navigation/ActiveNavLink';
import { SimpleSpinner } from '@components/common/loading/SimpleSpinner';
import { ArrowRightIcon, ChevronLeftIcon } from '@heroicons/react/outline';
import { useCourseUnitsMenuDispatcher } from './CourseUnitsMenu';
import { BaseBlurImage } from '@components/image/BaseBlurImage';
import { useCourseNextUnit } from '@hooks/courses/useCourseNextActivity';
import { Button } from '@skipper/ui';
import { ArrowLeftIcon } from '@heroicons/react/solid';
import { useQueryParams } from '@hooks/useQueryParams';

export function ActivityListItem({
    a,
    unitId,
    getSvg,
}: {
    a: Activity;
    unitId: string;
    getSvg: (act: Activity) => any;
}) {
    const { id } = useQueryParams(['id']);
    const SVGComponent = getSvg(a);
    return (
        <ActiveLink
            href={`/courses/${id}/units/${unitId}/activities/${a.id}`}
            activeStyle="text-teal-900"
            isFuzzyMatch>
            <div
                key={a.id}
                className="group flex overflow-hidden items-center py-3 px-2 hover:bg-teal-050 hover:text-teal-600 rounded-md">
                {SVGComponent}
                <div className="flex items-center px-4 truncate w-full">
                    <p className="truncate  group-hover:text-teal-900">{a?.item?.title}</p>
                </div>
            </div>
        </ActiveLink>
    );
}

export function NextItemButtons() {
    const dispatcher = useCourseUnitsMenuDispatcher();
    const [{ leftItemLink, rightItemLink }] = useCourseNextUnit();

    return (
        <div className="flex justify-between items-center mt-2 mb-1 px-3">
            {leftItemLink ? (
                <Button
                    type="button"
                    size="small"
                    shape="square"
                    variant={'white'}
                    onClick={() => leftItemLink?.id && dispatcher.setUnit(leftItemLink?.id)}>
                    <ArrowLeftIcon className="w-5 h-5 fill-passport" />
                </Button>
            ) : (
                <div />
            )}
            {rightItemLink ? (
                <Button
                    type="button"
                    size="small"
                    shape="square"
                    variant={'white'}
                    onClick={() => rightItemLink?.id && dispatcher.setUnit(rightItemLink?.id)}>
                    <ArrowRightIcon className="w-5 h-5 fill-passport" />
                </Button>
            ) : (
                <div />
            )}
        </div>
    );
}

export const UnitActivitiesMenuList = ({
    imageUrl,
    getSvg,
    iconColor,
    title,
    isLoading,
    activities,
    unitId,
}: {
    imageUrl: string;
    iconColor: string;
    title: string;
    isLoading: boolean;
    activities: Activity[];
    unitId: string;
    getSvg: (act: Activity) => any;
}) => {
    const { toggleMenu } = useCourseUnitsMenuDispatcher();
    return (
        <div className="flex flex-col w-full min-w-full overflow-hidden pb-1">
            <div className="flex flex-col px-2 gap-y-4 pb-4 border-b border-gray-100">
                <button
                    type="button"
                    onClick={toggleMenu}
                    className="flex w-full pt-4 gap-x-2 items-center focus:outline-none border-none">
                    <ChevronLeftIcon className="w-4 h-4 text-gray-500" />
                    <p className="text-xs font-bold text-gray-500">BACK TO UNITS MENU</p>
                </button>
                <div className="flex items-center w-full">
                    {imageUrl && (
                        <BaseBlurImage
                            src={imageUrl}
                            width={265}
                            height={100}
                            layout="fixed"
                            objectFit="cover"
                            className="rounded-md overflow-hidden flex items-center justify-center"
                        />
                    )}
                    {!imageUrl && (
                        <div
                            style={{
                                width: 260,
                                height: 100,
                                backgroundColor: iconColor,
                            }}
                            className="rounded-md"
                        />
                    )}
                </div>
                <div className="text-sm text-passport font-bold">{title}</div>
            </div>
            <div className="flex w-full h-full overflow-y-auto pb-2">
                <div className="w-full flex flex-col px-1 pt-0.5">
                    {isLoading && <SimpleSpinner />}
                    {activities?.length === 0 && (
                        <div className="flex items-center justify-center py-5 border-gray-300 text-sm">
                            No activities in this unit
                        </div>
                    )}
                    {activities?.map<any>((a) => {
                        return (
                            <ActivityListItem
                                key={a.id}
                                unitId={unitId}
                                a={a as any}
                                getSvg={getSvg}
                            />
                        );
                    })}
                    <NextItemButtons />
                </div>
            </div>
        </div>
    );
};
